import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useHomeDashboard } from "@/hooks/useHomeDashboard";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { StickyHeader } from "@/components/ui/StickyHeader";
import { HeaderIconButton } from "@/components/ui/HeaderIconButton";
import { FeedbackModal } from "@/components/FeedbackModal";
import { WelcomeSection } from "@/components/home/WelcomeSection";
import { ActiveChallengesList } from "@/components/home/ActiveChallengesList";
import { HowItWorksCard } from "@/components/home/HowItWorksCard";
import { UpcomingSection } from "@/components/home/UpcomingSection";
import { ActiveChallengeSection } from "@/components/home/ActiveChallengeSection";
import { OnboardingCarousel } from "@/components/onboarding";
import { getUserAvatarUrl } from "@/utils/avatar";
import { HomeChallengeCard } from "@/api/home";
import { MOCK_RUN_CHALLENGES } from "@/data/mockChallenges";

// Mock data for dev mode - Active Challenge Section
const MOCK_ACTIVE_CHALLENGE: HomeChallengeCard = {
  id: "mock-active-challenge",
  name: "Running 5km for 30 days",
  type: "daily_distance",
  totalDays: 30,
  dailyGoal: 5,
  stakeAmount: 50,
  participantCount: 25,
  startDate: "2025-12-01",
  endDate: "2025-12-30",
  status: "active",
  isUserParticipant: true,
  isUserCreator: false,
  currentDay: 24,
  daysCompleted: 24,
  progressPercentage: 80,
};

// Mock trendy challenges for display (converted from MOCK_RUN_CHALLENGES)
const MOCK_TRENDY_CHALLENGES: HomeChallengeCard[] = MOCK_RUN_CHALLENGES.map(
  (challenge) => ({
    id: challenge.id,
    name: challenge.name,
    type: challenge.type,
    totalDays: challenge.durationDays,
    dailyGoal: challenge.dailyGoal,
    stakeAmount: challenge.stakeAmount,
    participantCount: challenge.participantCount,
    startDate: challenge.startDate,
    endDate: challenge.endDate,
    status: challenge.status,
    isUserParticipant: false,
    isUserCreator: false,
    currentDay: 0,
    daysCompleted: 0,
    progressPercentage: 0,
  })
);

const isDev = import.meta.env.DEV;

export function Home() {
  const { isAuthenticated, login, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  // Fetch dashboard data from API
  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    error: dashboardError,
    refetch,
  } = useHomeDashboard();

  const handleCreateClick = () => {
    navigate("/create-challenge");
  };

  const handleProfileClick = () => {
    navigate("/profile");
  };

  const handleChallengeClick = (
    challengeId: string,
    isUserParticipant: boolean,
    status: string
  ) => {
    if (isUserParticipant && status === "active") {
      navigate(`/my-challenge/${challengeId}`);
    } else {
      navigate(`/challenge-preview/${challengeId}`);
    }
  };

  // Pre-login: Show onboarding carousel
  if (!isAuthenticated) {
    return (
      <OnboardingCarousel
        onLogin={login}
        onSkip={login}
        isLoading={isAuthLoading}
      />
    );
  }

  // Loading state
  if (isDashboardLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-light dark:bg-background-dark">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (dashboardError || !dashboard) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background-light dark:bg-background-dark p-4">
        <span className="material-symbols-outlined text-4xl text-red-500">
          error
        </span>
        <p className="text-center text-slate-600 dark:text-slate-400">
          {dashboardError || "Failed to load dashboard"}
        </p>
        <button
          onClick={() => refetch()}
          className="rounded-full bg-primary px-6 py-2 font-medium text-black"
        >
          Retry
        </button>
      </div>
    );
  }

  // Filter challenges by status for returning user variants
  // Include challenges where user is participant OR creator
  const userChallenges = dashboard.challengesSection.challenges.filter(
    (c) => c.isUserParticipant || c.isUserCreator
  );
  const upcomingChallenges = userChallenges.filter((c) => c.status === "upcoming");
  const activeChallenges = userChallenges.filter((c) => c.status === "active");

  // Trendy challenges = Mock challenges + real challenges (global discovery section)
  const trendyChallenges = [
    ...MOCK_TRENDY_CHALLENGES,
    ...dashboard.challengesSection.challenges,
  ];

  // Determine returning user variant
  // In dev mode, skip "upcoming only" to always show full dashboard with ActiveChallengeSection
  const hasUpcomingOnly = !isDev && upcomingChallenges.length > 0 && activeChallenges.length === 0;

  // First-time user: Show welcome/onboarding screen with new template
  if (dashboard.isFirstTimeUser) {
    return (
      <div className="relative flex h-auto min-h-screen w-full flex-col bg-[#f5f8f5] dark:bg-[#102217]">
        {/* Sticky Header with safe-area - Keep existing */}
        <StickyHeader
          leftContent={
            <button
              onClick={handleProfileClick}
              className="cursor-pointer transition-transform active:scale-95"
            >
              <img
                src={getUserAvatarUrl(
                  dashboard.user.avatarUrl,
                  dashboard.user.walletAddress,
                  "bottts",
                  40
                )}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover bg-gray-200 dark:bg-gray-700"
              />
            </button>
          }
          centerContent={
            <h1 className="text-xl font-extrabold tracking-tight text-text-light dark:text-text-dark">
              Welcome, {dashboard.user.username || "User"}!
            </h1>
          }
          rightContent={
            <HeaderIconButton
              icon="chat_bubble"
              onClick={() => setIsFeedbackOpen(true)}
              ariaLabel="Send feedback"
              variant="subtle"
            />
          }
        />

        {/* Welcome Section - Hero + Steps + Tagline */}
        <WelcomeSection />

        {/* Dark Panel Section - Contains challenges, CTA, and how it works */}
        <div className="relative z-10 pb-24 bg-[#f5fdf7] dark:bg-[#0e1713] rounded-t-[28px] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] dark:shadow-none">
          {/* Trendy Challenges */}
          <ActiveChallengesList
            sectionTitle={dashboard.challengesSection.sectionTitle || "Trendy Challenges"}
            sectionType={trendyChallenges.length > 0 ? "active" : "empty"}
            challenges={trendyChallenges}
            onChallengeClick={handleChallengeClick}
            variant="trendy"
          />

          {/* How It Works Card */}
          <HowItWorksCard onClick={() => {/* TODO: Link to tutorial */}} />
        </div>

        {/* Feedback Modal */}
        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setIsFeedbackOpen(false)}
        />
      </div>
    );
  }

  // Returning user - Upcoming Only: Has upcoming challenges but no active
  if (hasUpcomingOnly) {
    return (
      <div className="relative flex h-auto min-h-screen w-full flex-col bg-[#f5f8f5] dark:bg-[#102217]">
        {/* Sticky Header */}
        <StickyHeader
          leftContent={
            <button
              onClick={handleProfileClick}
              className="cursor-pointer transition-transform active:scale-95"
            >
              <img
                src={getUserAvatarUrl(
                  dashboard.user.avatarUrl,
                  dashboard.user.walletAddress,
                  "bottts",
                  40
                )}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover bg-gray-200 dark:bg-gray-700"
              />
            </button>
          }
          centerContent={
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium text-[#c5c1c1]">Welcome Back!</span>
              <span className="text-sm font-medium text-white">{dashboard.user.username || "User"}</span>
            </div>
          }
          rightContent={
            <HeaderIconButton
              icon="chat_bubble"
              onClick={() => setIsFeedbackOpen(true)}
              ariaLabel="Send feedback"
              variant="subtle"
            />
          }
        />

        {/* Welcome Section - Hero + Steps + Tagline */}
        <WelcomeSection />

        {/* Dark Panel Section */}
        <div className="relative z-10 flex-1 bg-[#0e1713] rounded-t-[14px] pb-24">
          {/* Upcoming Challenges Section */}
          <UpcomingSection
            challenges={upcomingChallenges}
            onChallengeClick={(id) => navigate(`/challenge-preview/${id}`)}
          />

          {/* Trendy Challenges - Discovery */}
          <ActiveChallengesList
            sectionTitle="Trendy Challenges"
            sectionType={trendyChallenges.length > 0 ? "active" : "empty"}
            challenges={trendyChallenges}
            onChallengeClick={handleChallengeClick}
            variant="trendy"
          />

          {/* Create Challenge CTA */}
          <div className="flex justify-center px-6 py-5">
            <button
              onClick={handleCreateClick}
              className="px-5 py-2.5 bg-[#21f219] border border-white/10 rounded-full text-black text-[10px] font-semibold font-dm-sans hover:brightness-110 transition-all active:scale-[0.98]"
            >
              Create your own challenge !
            </button>
          </div>

          {/* How It Works Card */}
          <HowItWorksCard onClick={() => {/* TODO: Link to tutorial */}} />
        </div>

        {/* Feedback Modal */}
        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setIsFeedbackOpen(false)}
        />
      </div>
    );
  }

  // Returning user: Show unified dashboard with dark panel
  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col bg-[#f5f8f5] dark:bg-[#102217]">
      {/* Sticky Header */}
      <StickyHeader
        leftContent={
          <button
            onClick={handleProfileClick}
            className="cursor-pointer transition-transform active:scale-95"
          >
            <img
              src={getUserAvatarUrl(
                dashboard.user.avatarUrl,
                dashboard.user.walletAddress,
                "bottts",
                40
              )}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover bg-gray-200 dark:bg-gray-700"
            />
          </button>
        }
        centerContent={
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-[#c5c1c1]">Welcome Back!</span>
            <span className="text-sm font-medium text-white">{dashboard.user.username || "User"}</span>
          </div>
        }
        rightContent={
          <HeaderIconButton
            icon="chat_bubble"
            onClick={() => setIsFeedbackOpen(true)}
            ariaLabel="Send feedback"
            variant="subtle"
          />
        }
      />

      {/* Welcome Section - Hero + Steps + Tagline */}
      <WelcomeSection />

      {/* Dark Panel Section */}
      <div className="relative z-10 flex-1 bg-[#0e1713] rounded-t-[14px] pb-24">
        {/* Active Challenge Section */}
        {(activeChallenges.length > 0 || isDev) && (
          <ActiveChallengeSection
            challenge={activeChallenges[0] || MOCK_ACTIVE_CHALLENGE}
            challengeNumber={1}
            onChallengeClick={(id) => navigate(`/my-challenge/${id}`)}
          />
        )}

        {/* Upcoming Challenges Section */}
        {upcomingChallenges.length > 0 && (
          <UpcomingSection
            challenges={upcomingChallenges}
            onChallengeClick={(id) => navigate(`/challenge-preview/${id}`)}
          />
        )}

        {/* Trendy Challenges - Discovery */}
        <ActiveChallengesList
          sectionTitle="Trendy Challenges"
          sectionType={trendyChallenges.length > 0 ? "active" : "empty"}
          challenges={trendyChallenges}
          onChallengeClick={handleChallengeClick}
          variant="trendy"
        />

        {/* Create Challenge CTA */}
        <div className="flex justify-center px-6 py-5">
          <button
            onClick={handleCreateClick}
            className="px-5 py-2.5 bg-[#21f219] border border-white/10 rounded-full text-black text-[10px] font-semibold font-dm-sans hover:brightness-110 transition-all active:scale-[0.98]"
          >
            Create your own challenge !
          </button>
        </div>

        {/* How It Works Card */}
        <HowItWorksCard onClick={() => {/* TODO: Link to tutorial */}} />
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
    </div>
  );
}
